import { getDb, loadFirestoreModule } from "@/lib/firebase/db";
import type { ArticleCartItem, BenglasCartItem, GlassCartItem, IsolatieglasCartItem, ShowerCartItem } from "@/features/cart/types";

/** Een bewaard cart-item zonder id (glas op maat, product, vacuümglas, isolatieglas of douche). */
export type SavedItem = Omit<GlassCartItem, "id"> | Omit<ArticleCartItem, "id"> | Omit<BenglasCartItem, "id"> | Omit<IsolatieglasCartItem, "id"> | Omit<ShowerCartItem, "id">;

/** Een bewaard item = de cart-payload (glas op maat óf een product), lossless herbestellen. */
export interface SavedConfig {
  id: string;
  savedAt: number;
  item: SavedItem;
}

/** Eén opgeslagen adres in het adresboek (verzend- of factuuradres). */
export interface SavedAddress {
  id: string;
  firstName: string;
  lastName: string;
  street: string; // straat + huisnummer
  postalCode: string;
  city: string;
  country: string; // "NL" / "BE"
  isDefault?: boolean;
}

/** Het klantprofiel in Firestore: /customers/{uid}. Owner mag lezen/schrijven (rules). */
export interface CustomerProfile {
  firstName: string;
  lastName: string;
  phone: string;
  /** Zakelijk account → prijzen excl. btw, plus bedrijfsvelden. */
  isBusiness: boolean;
  companyName: string;
  kvk: string;
  vatNumber: string;
  /** Zakelijke verificatiestatus. "verified" = btw geldig in VIES; "pending" = check kon niet
   *  (VIES-storing) → admin beoordeelt; "revoked" = door admin ingetrokken (geen B2B-voordelen). */
  businessStatus?: "verified" | "pending" | "revoked";
  /** Bij VIES-verificatie teruggekregen bedrijfsnaam/adres (informatief voor de admin). */
  businessVerifiedName?: string;
  businessVerifiedAt?: number;
  btwPreference: "incl" | "excl";
  newsletter: boolean;
  addresses: SavedAddress[];
  savedConfigs: SavedConfig[];
  updatedAt?: number;
}

export const EMPTY_PROFILE: CustomerProfile = {
  firstName: "",
  lastName: "",
  phone: "",
  isBusiness: false,
  companyName: "",
  kvk: "",
  vatNumber: "",
  btwPreference: "incl",
  newsletter: false,
  addresses: [],
  savedConfigs: [],
};

async function ref(uid: string) {
  const [{ doc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
  return doc(db, "customers", uid);
}

/** Profiel ophalen; bestaat het nog niet, dan lege defaults. */
export async function getProfile(uid: string): Promise<CustomerProfile> {
  const { getDoc } = await loadFirestoreModule();
  const snap = await getDoc(await ref(uid));
  if (!snap.exists()) return { ...EMPTY_PROFILE };
  return { ...EMPTY_PROFILE, ...(snap.data() as Partial<CustomerProfile>) };
}

/** Profiel (deels) opslaan — merge, zodat losse velden los bewaard kunnen worden. */
export async function saveProfile(uid: string, patch: Partial<CustomerProfile>): Promise<void> {
  const { setDoc } = await loadFirestoreModule();
  await setDoc(await ref(uid), { ...patch, updatedAt: Date.now() }, { merge: true });
}

/** Adresboek opslaan (de hele lijst, na toevoegen/bewerken/verwijderen). */
export async function saveAddresses(uid: string, addresses: SavedAddress[]): Promise<void> {
  await saveProfile(uid, { addresses });
}

/** Klein, botsingsarm id voor een nieuw adres (geen Math.random nodig in build). */
export function newAddressId(existing: SavedAddress[]): string {
  let n = existing.length + 1;
  const ids = new Set(existing.map((a) => a.id));
  while (ids.has(`adr-${n}`)) n += 1;
  return `adr-${n}`;
}

/** Een samenstelling bewaren (vooraan in de lijst). */
export async function addSavedConfig(uid: string, item: SavedConfig["item"]): Promise<string> {
  const current = (await getProfile(uid)).savedConfigs;
  // Firestore weigert `undefined`-velden; JSON-rondje strip ze (bv. ontbrekende imageUrl).
  const clean = JSON.parse(JSON.stringify(item)) as SavedConfig["item"];
  const cfg: SavedConfig = { id: `cfg-${Date.now()}`, savedAt: Date.now(), item: clean };
  await saveProfile(uid, { savedConfigs: [cfg, ...current] });
  return cfg.id;
}

/** Een bewaarde samenstelling verwijderen. */
export async function removeSavedConfig(uid: string, id: string): Promise<void> {
  const current = (await getProfile(uid)).savedConfigs;
  await saveProfile(uid, { savedConfigs: current.filter((c) => c.id !== id) });
}

/** Persoonlijke korting (admin-only gezet; klant kan 'm alleen lezen). */
export interface CustomerDiscount {
  kind: "percent" | "amount";
  value: number; // percent (1–100) of centen
  note?: string;
  expiresAt?: number | null; // epoch ms; null = altijd geldig
}

/** Eigen korting; verlopen kortingen geven null terug. */
export async function getMyDiscount(uid: string): Promise<CustomerDiscount | null> {
  const [{ doc, getDoc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
  const snap = await getDoc(doc(db, "customerDiscounts", uid));
  if (!snap.exists()) return null;
  const d = snap.data() as Partial<CustomerDiscount>;
  if (d.kind !== "percent" && d.kind !== "amount") return null;
  if (d.expiresAt && d.expiresAt < Date.now()) return null; // verlopen
  return { kind: d.kind, value: Number(d.value ?? 0), note: d.note, expiresAt: d.expiresAt ?? null };
}
